import { playButtonField } from '../_shared/videoPlayButton.js';
import { textColorFields } from '../_shared/textColor.js';
import { mediaOverlayFields } from '../_shared/mediaOverlay.js';
import { mediaRadiusField, universalFields } from '../_shared/universal.js';
/**
 * flip-cards — LES CARTES PIVOTANTES. Un rail de cartes-photo : le titre se lit SUR la photo, le « + »
 * fait pivoter la carte, et le texte est AU DOS. Référence apportée par la fondatrice le 19/09/2026 :
 * la page HUAWEI Band 10/11 (« Workout Buddy on Your Wrist »), markup à l'appui.
 *
 * ── CE QU'ON REPREND DE LA RÉFÉRENCE, ET CE QU'ON NE REPREND PAS ───────────────────────────────────────
 *
 * Repris : la bande COLLANTE (leur `.sticky-content`, hauteur et `top` pilotés au défilement) — le grand
 * titre s'efface pendant que les cartes grandissent ; le rail horizontal ; la carte qui PIVOTE sur son
 * « + ».
 *
 * Pas repris : chez Huawei le pivot n'existe que sur téléphone (`icon-plus` et `mob-content-back` sont en
 * `mob-show` ; sur grand écran le paragraphe est POSÉ SOUS la carte, hors de la carte). Ici il pivote
 * partout — décision de la fondatrice, 19/09 : la carte est l'objet, la page reste épurée.
 *
 * ── POURQUOI UNE SECTION À PART, ET PAS UN RÉGLAGE DE MULTICOLONNES ────────────────────────────────────
 *
 * `custom-columns` sait déjà aligner N colonnes média + titre + texte, et son carrousel de bureau est
 * livré (15/09). Mais une colonne y est une PILE : média, puis titre, puis texte, chacun sur sa ligne.
 * Ici la carte est UNE SURFACE — le texte se lit sur la photo, et le corps n'existe pas tant qu'on n'a
 * pas retourné la carte. Ce n'est pas une option de mise en page de la pile, c'est une autre anatomie :
 * deux faces, un axe de rotation, et une hauteur qui ne dépend plus de la longueur du texte.
 *
 * ── LE PIVOT EST EN ZÉRO JAVASCRIPT ────────────────────────────────────────────────────────────────────
 *
 * Une case à cocher masquée par carte, POSÉE DANS le bouton « + » qu'on clique, et `li:has(… :checked)`
 * retourne la carte. Aucun îlot : ça marche en SSR, script coupé, et À L'IDENTIQUE dans le canvas du
 * builder, qui n'arme aucun îlot. Seules la bande collante et les flèches du rail demandent un moteur,
 * et les deux sont des raffinements : sans eux la section reste une grille de cartes qui se retournent.
 *
 * (Case DANS le bouton, pas groupée ailleurs : cocher met au foyer, et le navigateur défile jusqu'à
 * l'élément au foyer — précédent `feature-gallery`, 15/09, jusqu'à 640px de saut dans le canvas.)
 */
const CONTENT = 'Content';
const LAYOUT = 'Layout';
const MOBILE = 'Mobile';
const MOTION = 'Motion';
const B_MEDIA = 'Media';
const B_TEXT = 'Text';
/** Les formes de carte, et le rapport que chaque étiquette PROMET (voir Component.tsx : `RATIO`). */
const SHAPES = [
    { value: 'portrait', label: 'Portrait (4:5)' },
    { value: 'square', label: 'Square' },
    { value: 'landscape', label: 'Landscape (3:2)' },
    { value: 'tall', label: 'Tall (9:16)' },
];
export const flipCardsSchema = {
    type: 'flip-cards',
    name: 'Flip cards',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── Content ─────────────────────────────────────────────────────────────
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: CONTENT,
            default: '',
            aiWritable: true,
            maxLength: 80,
            info: 'With the sticky entrance on, this is the title that fades away as the cards grow into place.',
        },
        // ── Layout ──────────────────────────────────────────────────────────────
        {
            id: 'columnsDesktop',
            type: 'range',
            label: 'Cards in view',
            group: LAYOUT,
            min: 2,
            max: 4,
            step: 1,
            default: 3,
            info: 'On a large screen. Extra cards are reached with the arrows — the rail never wraps onto a second row.',
        },
        {
            id: 'cardShape',
            type: 'select',
            label: 'Card shape',
            group: LAYOUT,
            default: 'portrait',
            options: SHAPES,
            info: 'Every card keeps this shape whatever its text is worth — the body copy lives on the back, so it can never stretch the row.',
        },
        {
            id: 'gap',
            type: 'range',
            label: 'Space between cards',
            group: LAYOUT,
            min: 0,
            max: 48,
            step: 2,
            unit: 'px',
            default: 16,
        },
        // ── Mobile ──────────────────────────────────────────────────────────────
        {
            id: 'mobileCardShape',
            type: 'select',
            label: 'Card shape',
            group: MOBILE,
            default: 'inherit',
            options: [{ value: 'inherit', label: 'Same as desktop' }, ...SHAPES],
            info: 'A phone shows one card at a time, so a wide shape that works in a row of three often reads as a letterbox here.',
        },
        // ── Motion ──────────────────────────────────────────────────────────────
        {
            id: 'stickyBand',
            type: 'checkbox',
            label: 'Sticky entrance',
            group: MOTION,
            default: true,
            info: 'The band holds still for one screen of scrolling: the heading fades out while the cards grow into place. Turned off — or for a visitor who asked for less motion — the cards are simply there.',
        },
        // ── Overlay ─────────────────────────────────────────────────────────────
        // Les champs de voile PARTAGÉS, avec un seul défaut changé : `bottom` au lieu de `none`. Le titre de
        // cette section se lit SUR la photo — un voile qu'il faudrait allumer soi-même livrerait, par défaut,
        // du texte blanc sur un ciel blanc. Ailleurs le voile est un choix ; ici c'est la condition de lisibilité.
        ...mediaOverlayFields().map((f) => f.type === 'select' && f.id === 'mediaOverlayStyle' ? { ...f, default: 'bottom' } : f),
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    blocks: [
        {
            type: 'card',
            name: 'Card',
            max: 12,
            fields: [
                { id: 'image', type: 'image', label: 'Image', group: B_MEDIA },
                {
                    id: 'video',
                    type: 'video',
                    label: 'Video',
                    group: B_MEDIA,
                    info: 'Shown instead of the image, and it plays like one — silent and looping. The image above stays as its poster.',
                },
                // Le disque lecture/pause de CETTE carte (20/09/2026, fondatrice : « add this config to all
                // components that has video in them »). Éteint par défaut : une carte-clip est une photo qui
                // bouge, et une bande de six cartes portant six disques est un mur de boutons.
                playButtonField({
                    defaultOn: false,
                    group: B_MEDIA,
                    visibleWhen: { field: 'video', empty: false },
                }),
                {
                    id: 'title',
                    type: 'text',
                    label: 'Heading',
                    group: B_TEXT,
                    default: '',
                    aiWritable: true,
                    maxLength: 60,
                    info: 'Read on the photo, on the front of the card.',
                },
                {
                    id: 'stat',
                    type: 'text',
                    label: 'Figure',
                    group: B_TEXT,
                    default: '',
                    aiWritable: true,
                    maxLength: 12,
                    info: 'A number to hold the eye — “95%”, “5 ATM”, “18 h”. Left empty, nothing is drawn.',
                },
                {
                    id: 'statCaption',
                    type: 'text',
                    label: 'What the figure says',
                    group: B_TEXT,
                    default: '',
                    aiWritable: true,
                    maxLength: 40,
                    info: 'The small line under the figure. A figure with no caption is a riddle.',
                },
                {
                    id: 'text',
                    type: 'textarea',
                    label: 'Back of the card',
                    group: B_TEXT,
                    default: '',
                    aiWritable: true,
                    maxLength: 420,
                    info: 'What the visitor reads after tapping “+”. A card with nothing here does not offer the button at all.',
                },
            ],
        },
    ],
};
export const flipCardsPresets = [
    {
        name: 'Flip cards',
        settings: {
            heading: '@t:section.flip_cards_heading',
            columnsDesktop: 3,
            cardShape: 'portrait',
            gap: 16,
            mobileCardShape: 'inherit',
            stickyBand: true,
            mediaOverlayStyle: 'bottom',
            mediaOverlayOpacity: 85,
            mediaOverlaySize: 60,
            colorScheme: 'light',
            width: 'wide',
            paddingTop: 72,
            paddingBottom: 72,
        },
        blocks: [
            {
                type: 'card',
                settings: {
                    title: '@t:section.flip_cards_c1_title',
                    text: '@t:section.flip_cards_c1_text',
                },
            },
            {
                type: 'card',
                settings: {
                    title: '@t:section.flip_cards_c2_title',
                    stat: '95%',
                    statCaption: '@t:section.flip_cards_c2_caption',
                    text: '@t:section.flip_cards_c2_text',
                },
            },
            {
                type: 'card',
                settings: {
                    title: '@t:section.flip_cards_c3_title',
                    text: '@t:section.flip_cards_c3_text',
                },
            },
        ],
    },
];
/**
 * Comment la section se présente dans « Ajouter une section ». Elle vit ICI et pas dans `index.ts` :
 * `index.ts` tire `Component.tsx`, et le builder lit `meta` pour les soixante-quatre sections afin de
 * peindre le sélecteur, alors qu'il n'en rend qu'une poignée.
 */
export const flipCardsMeta = {
    label: 'Flip cards',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map